import "./image-picker.scss";
import { useRef, useState } from "react";
import { Image, Trash } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { FileUpload, FileUploadHandlerEvent, FileUploadOptions } from "primereact/fileupload";
import { Button } from "primereact/button";

import { cn } from "utils/helpers";
import { uploadPhoto, removeImageIfOrphaned } from "utils/api";
import { Spinner } from "utils/ui/Spinner";
import { useAppDispatch, useAppSelector, addImageManifestEntry } from "utils/store";
import { resolveImageSrc } from "utils/templates/image-manifest";
import { Images } from "utils/services";
import { environment } from "environments/environment";

export const ImagePicker = ({
  imageId,
  onImageIdChange,
}: {
  imageId?: string;
  onImageIdChange: (imageId?: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const storageToken = useAppSelector((state) => state.templateBuilderSlice.storageToken);
  const manifestEntry = useAppSelector((state) =>
    state.templateBuilderSlice.imageManifest.find((e) => e.id === imageId)
  );
  const imgSrc = resolveImageSrc(manifestEntry, storageToken);

  const [isLoading, setIsLoading] = useState(false);
  const fileUploadRef = useRef<FileUpload>(null);

  const customBase64Uploader = async (event: FileUploadHandlerEvent) => {
    if (!event.files.length) return;

    const file: File = event.files[0];

    setIsLoading(true);

    const resizedImage = await Images.createResizedFile(file, 1024);

    if (!environment.production) {
      console.log(`File size reduced: ${file.size} > ${resizedImage.size}`);
    }

    const result = await uploadPhoto(resizedImage);

    if (result) {
      const newImageId = uuidv4();
      dispatch(
        addImageManifestEntry({ id: newImageId, docId: result.documentId, filename: result.filename, url: result.url })
      );
      onImageIdChange(newImageId);
    } else {
      fileUploadRef.current?.clear();
    }

    setIsLoading(false);
  };

  const onDeleteImage = async (): Promise<void> => {
    if (imageId) {
      await dispatch(removeImageIfOrphaned(imageId));
    }
    onImageIdChange(undefined);
  };

  const headerTemplate = ({ className, chooseButton }: any) => {
    return <div className={cn(className)}>{chooseButton}</div>;
  };

  const itemTemplate = (file: any, props: any) => {
    return (
      <div className="flex align-items-center flex-wrap">
        <div className="flex align-items-center" style={{ width: "40%" }}>
          <img alt={file.name} role="presentation" src={file.objectURL} width={100} />
          <span className="flex flex-column text-left ml-3">
            {file.name}
            <small>{new Date().toLocaleDateString()}</small>
          </span>
        </div>
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="p-6 flex flex-col items-center justify-center" style={{ color: "var(--surface-600)" }}>
        <div>
          {!isLoading && <Image size={64} strokeWidth={1} />}
          {isLoading && <Spinner style={{ height: 48, width: 48, margin: "8px" }} />}
        </div>
        <p>Drag and drop file to upload.</p>
      </div>
    );
  };

  const chooseOptions: FileUploadOptions = {
    className:
      "py-2 px-4 text-[var(--surface-800)] bg-[var(--surface-200)] border border-[var(--surface-border)] hover:text-[var(--surface-900)] hover:bg-[var(--surface-300)] active:text-[var(--surface-900)] active:bg-[var(--surface-300)]",
  };

  return (
    <div className="image-picker-wrapper bg-[var(--surface-100)] rounded-[var(--border-radius)] h-[260px]">
      {imgSrc && (
        <div className="group relative h-[260px] p-2 flex items-center justify-center">
          <div className="absolute top-[10px] right-[10px] opacity-0 pointer-events-none transition-opacity duration-200 ease-in-out group-hover:opacity-100 group-hover:pointer-events-auto">
            <Button
              title="Remove image"
              size="small"
              className="p-button-rounded p-button-danger p-2"
              onClick={onDeleteImage}
            >
              <Trash size={22} />
            </Button>
          </div>
          <img className="max-h-[96%] w-auto max-w-[96%] m-auto" src={imgSrc} alt="" />
        </div>
      )}

      {!imgSrc && (
        <FileUpload
          ref={fileUploadRef}
          name="demo[]"
          multiple={false}
          auto={true}
          accept="image/*"
          customUpload
          uploadHandler={customBase64Uploader}
          headerTemplate={headerTemplate}
          itemTemplate={itemTemplate}
          emptyTemplate={emptyTemplate}
          chooseOptions={chooseOptions}
        />
      )}
    </div>
  );
};
